'use client'

import * as React from 'react'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type EmissionListItem = {
  id: string
  dispatchId: string | null
  legId: string | null
  calculationMode: string
  roadKm: number
  freightWeightKg: number
  totalCo2Kg: number
  vehicleEuroClass: string | null
  generatedAt: string
}

type EmissionsListResponse = {
  items: EmissionListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export default function EmissionsListPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()

  const { data, isLoading, error } = useQuery({
    queryKey: ['freight.emissions', scopeVersion],
    queryFn: async () => {
      const response = await apiCall<EmissionsListResponse>('/api/freight/emissions?pageSize=100')
      if (!response.ok) {
        await raiseCrudError(response.response, t('emissions.pages.list.loadError', 'Failed to load emission reports.'))
      }
      return response.result ?? { items: [], page: 1, pageSize: 100, total: 0, totalPages: 1 }
    },
  })

  const columns = React.useMemo<ColumnDef<EmissionListItem>[]>(() => [
    {
      accessorKey: 'generatedAt',
      header: t('emissions.table.columns.generatedAt', 'Generated'),
      cell: ({ row }) => new Date(row.original.generatedAt).toLocaleString(),
    },
    {
      accessorKey: 'calculationMode',
      header: t('emissions.table.columns.calculationMode', 'Mode'),
      cell: ({ row }) =>
        t(`emissions.lookups.calculationMode.${row.original.calculationMode}`, row.original.calculationMode),
    },
    {
      accessorKey: 'dispatchId',
      header: t('emissions.table.columns.dispatch', 'Dispatch'),
      cell: ({ row }) => row.original.dispatchId ?? '—',
    },
    {
      accessorKey: 'legId',
      header: t('emissions.table.columns.leg', 'Leg'),
      cell: ({ row }) => row.original.legId ?? '—',
    },
    {
      accessorKey: 'roadKm',
      header: t('emissions.table.columns.roadKm', 'Road km'),
      cell: ({ row }) => row.original.roadKm.toFixed(1),
    },
    {
      accessorKey: 'freightWeightKg',
      header: t('emissions.table.columns.freightWeightKg', 'Freight kg'),
      cell: ({ row }) => row.original.freightWeightKg.toFixed(1),
    },
    {
      accessorKey: 'totalCo2Kg',
      header: t('emissions.table.columns.totalCo2Kg', 'CO2 kg'),
      cell: ({ row }) => row.original.totalCo2Kg.toFixed(3),
    },
    {
      accessorKey: 'vehicleEuroClass',
      header: t('emissions.table.columns.vehicleEuroClass', 'Euro class'),
      cell: ({ row }) => row.original.vehicleEuroClass ?? '—',
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <DataTable
          title={t('emissions.pages.list.title', 'Emissions')}
          columns={columns}
          data={data?.items ?? []}
          rowActions={(row) => (
            <RowActions items={[
              { id: 'open', label: t('emissions.actions.open', 'Open'), href: `/backend/emissions/${row.id}` },
            ]} />
          )}
          pagination={{
            page: data?.page ?? 1,
            pageSize: data?.pageSize ?? 100,
            total: data?.total ?? 0,
            totalPages: data?.totalPages ?? 1,
            onPageChange: () => undefined,
          }}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : null}
          emptyState={t('emissions.pages.list.empty', 'No emission reports have been generated yet.')}
        />
        <div className="mt-4 text-sm text-muted-foreground">
          <Link href="/api/freight/emissions" className="underline underline-offset-4">
            {t('emissions.pages.list.apiHint', 'Backend consumers can also read the raw JSON list API.')}
          </Link>
        </div>
      </PageBody>
    </Page>
  )
}
